/**
 * Issuer Hub Context
 *
 * P3-01: React context providing issuer identity and write-permission state to
 * all components inside authenticated issuer hub routes.
 *
 * Consumers use `useIssuerHubContext()` — it throws if called outside of an
 * `<IssuerHubProvider>` (i.e. outside a route wrapped by `<IssuerHubGuard>`).
 * This ensures the context is never `null` at call sites, so components can
 * destructure values directly without null-checks.
 */

import { createContext, useContext } from "react";
import type { IssuerHubContextType } from "@/types/IssuerPipeline";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const IssuerHubContext = createContext<IssuerHubContextType | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface IssuerHubProviderProps {
  value: IssuerHubContextType;
  children: React.ReactNode;
}

/** Issuer Hub Provider component. */
export function IssuerHubProvider({ value, children }: IssuerHubProviderProps) {
  return <IssuerHubContext.Provider value={value}>{children}</IssuerHubContext.Provider>;
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Returns the issuer hub context value for the current authenticated user.
 *
 * @throws If called outside an `<IssuerHubProvider>` tree (i.e. outside a
 *   route guarded by `<IssuerHubGuard>`). This is a programming error and
 *   should surface clearly in development.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIssuerHubContext(): IssuerHubContextType {
  const ctx = useContext(IssuerHubContext);
  if (!ctx) {
    throw new Error(
      "useIssuerHubContext must be used within an IssuerHubProvider. " +
        "Wrap your component tree with <IssuerHubGuard>.",
    );
  }
  return ctx;
}
